import * as React from 'react';
import * as Yup from 'yup';
import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik';
import { initialValues, linkDefaults } from './default-values';

const optionalUrl = Yup.string().url('Must be a valid URL').nullable();

const validationSchema = Yup.object().shape({
  title: Yup.string().required(`Can't be blank`),
  description: Yup.string(),
  icon: optionalUrl,
  breadcrumbs: Yup.array().of(
    Yup.object({
      text: Yup.string(),
      url: optionalUrl,
    })
  ),
  extraResources: Yup.object({
    label: Yup.string(),
    items: Yup.array().of(
      Yup.object({
        text: Yup.string(),
        url: optionalUrl,
      })
    ),
  }),
});

// Formik normalizes dots in field names to underscores when generating ids,
// so we mirror that to keep `htmlFor` matching the rendered input id.
function fieldId(name) {
  return name.replace(/\./g, '_');
}

function LinksFieldArray({ name, links, push, remove, addLabel }) {
  return (
    <div className='nv-component-settings-form__links'>
      {links && links.length > 0 && links.map((_, idx) => {
        const textName = `${name}.${idx}.text`;
        const urlName = `${name}.${idx}.url`;
        const textId = fieldId(textName);
        const urlId = fieldId(urlName);

        return (
          <div className='row align-items-end' key={idx}>
            <div className='col-5'>
              <label className='form-label' htmlFor={textId}>Text</label>
              <Field id={textId} name={textName} className='form-control form-control-sm'/>
              <ErrorMessage name={textName} component='p' className='field-error mt-2 mb-0'/>
            </div>
            <div className='col-6'>
              <label className='form-label' htmlFor={urlId}>URL</label>
              <Field id={urlId} name={urlName} className='form-control form-control-sm'/>
              <ErrorMessage name={urlName} component='p' className='field-error mt-2 mb-0'/>
            </div>

            <div className='col-1 my-2'>
              <button
                type='button'
                className='btn btn-danger btn-sm'
                aria-label='Remove item'
                onClick={() => remove(idx)}
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
      <button
        type='button'
        className='btn btn-outline-primary btn-sm mt-3'
        onClick={() => push({ ...linkDefaults })}
      >
        + {addLabel}
      </button>
    </div>
  );
}

export function SettingsForm({ submitHandler, defaultValues = initialValues }) {
  return (
    <div className='nv-component-settings-form'>
      <Formik
        initialValues={defaultValues}
        validationSchema={validationSchema}
        onSubmit={values => submitHandler(values)}
      >
        {({ values }) => (
          <Form>
            <div className='nv-component-settings-form__fields'>
              <div className='nv-component-settings-form__section'>
                <div className='nv-component-settings-form__field'>
                  <label className='form-label' htmlFor='title'>Title</label>
                  <Field id='title' name='title' className='form-control form-control-sm'/>
                  <ErrorMessage name='title' component='p' className='field-error mt-2 mb-0'/>
                </div>

                <div className='nv-component-settings-form__field'>
                  <label className='form-label' htmlFor='description'>Description</label>
                  <Field as='textarea' id='description' name='description' className='form-control form-control-sm' rows={3}/>
                  <ErrorMessage name='description' component='p' className='field-error mt-2 mb-0'/>
                </div>

                <div className='nv-component-settings-form__field'>
                  <label className='form-label' htmlFor='icon'>Icon URL</label>
                  <Field id='icon' name='icon' className='form-control form-control-sm'/>
                  <ErrorMessage name='icon' component='p' className='field-error mt-2 mb-0'/>
                </div>
              </div>

              <div className='nv-component-settings-form__section'>
                <details>
                  <summary>Breadcrumbs</summary>
                  <FieldArray name='breadcrumbs'>
                    {({ push, remove }) => (
                      <LinksFieldArray
                        name='breadcrumbs'
                        links={values.breadcrumbs}
                        push={push}
                        remove={remove}
                        addLabel='Add breadcrumb'
                      />
                    )}
                  </FieldArray>
                </details>
              </div>

              <div className='nv-component-settings-form__section'>
                <details>
                  <summary>Extra resources</summary>
                  <div className='nv-component-settings-form__field'>
                    <label className='form-label' htmlFor={fieldId('extraResources.label')}>Label</label>
                    <Field
                      id={fieldId('extraResources.label')}
                      name='extraResources.label'
                      className='form-control form-control-sm'
                    />
                    <ErrorMessage name='extraResources.label' component='p' className='field-error mt-2 mb-0'/>
                  </div>

                  <FieldArray name='extraResources.items'>
                    {({ push, remove }) => (
                      <LinksFieldArray
                        name='extraResources.items'
                        links={values.extraResources?.items}
                        push={push}
                        remove={remove}
                        addLabel='Add resource'
                      />
                    )}
                  </FieldArray>
                </details>
              </div>

            </div>

            <div className='nv-component-settings-form__actions'>
              <button type='submit' className='btn btn-info btn-sm'>Save settings</button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
